import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Input, Modal, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { PERMISSIONS } from "../../../../constants/permission";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { IMasterData } from "../../../../interfaces/master-data";
import { debounce } from "../../../../utils/helper";
import { NOMINAL_CODE_TYPES } from "../../../../constants/enum";
import NominalCodesList from "./list.nominal-codes";
import CreateAndEditNominalCodes from "./create-and-edit";

interface NominalCodeTabProps {
  type: NOMINAL_CODE_TYPES;
}

const NominalCodeTabPage: React.FC<NominalCodeTabProps> = ({ type }) => {
  const [openModal, setOpenModal] = useState(false);
  const [titleModal, setTitleModal] = useState<string>("Add nominalCode");
  const [selectNominalCode, setSelectNominalCode] = useState<IMasterData>();
  const [okNominalCodeTriggered, setOkValueTriggered] = useState(true);
  const hasPermissionEditor = useHasPermission([PERMISSIONS.MASTER_DATA_EDITOR]);
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);

  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const suffix =
    type === NOMINAL_CODE_TYPES.DEBIT ? "Debit" : type === NOMINAL_CODE_TYPES.CREDIT ? "Credit" : "Unders & Overs";
  const handleCreate = () => {
    setTitleModal(`Add Nominal Code - ${suffix}`);
    setSelectNominalCode(undefined);
    setOpenModal(true);
  };

  const handleEdit = (nominalCode: IMasterData) => {
    setTitleModal(`Edit Nominal Code - ${suffix}`);
    setSelectNominalCode(nominalCode);
    setOpenModal(true);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleChangeShowActiveOnly = useCallback((checked: boolean) => {
    setShowActiveOnly(checked);
  }, []);

  return (
    <div className="page-master-data">
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Nominal Value"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 400 }}
          size="large"
          allowClear
        />
        <Flex gap={22} align="center">
          <div>
            <span style={{ marginRight: 6, alignItems: "center" }}>Show Active Only</span>
            <Switch checked={showActiveOnly} onChange={handleChangeShowActiveOnly} />
          </div>
          {hasPermissionEditor && (
            <Button type="primary" onClick={handleCreate}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </Button>
          )}
        </Flex>
      </Flex>

      <NominalCodesList
        onEditNominalCode={handleEdit}
        trigger={okNominalCodeTriggered}
        onDoneTrigger={() => setOkValueTriggered(false)}
        debouncedSearchValue={debouncedSearchValue}
        type={type}
        showActiveOnly={showActiveOnly}
      />

      {openModal && (
        <Modal open={openModal} onCancel={() => setOpenModal(false)} footer={null} width={560} title={titleModal}>
          <CreateAndEditNominalCodes
            onClose={(isTrigger) => {
              setOpenModal(false);
              if (isTrigger) {
                setOkValueTriggered((prev) => !prev);
              }
            }}
            masterData={selectNominalCode}
            type={type}
          />
        </Modal>
      )}
    </div>
  );
};

export default NominalCodeTabPage;
